import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { History, RefreshCw, X } from 'lucide-react';

interface SessionResumePromptProps {
  userTurns: number;
  lastUpdatedAt: string;
  reachedBranch: boolean;
  onContinue: () => void;
  onStartFresh: () => void;
}

const formatRelative = (iso: string, isZh: boolean) => {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return isZh ? '刚刚' : 'just now';
  if (mins < 60) return isZh ? `${mins} 分钟前` : `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return isZh ? `${hours} 小时前` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return isZh ? `${days} 天前` : `${days}d ago`;
};

const SessionResumePrompt: React.FC<SessionResumePromptProps> = ({
  userTurns,
  lastUpdatedAt,
  reachedBranch,
  onContinue,
  onStartFresh,
}) => {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');

  return (
    <div className="mx-auto my-3 max-w-2xl rounded-xl border border-amber-200 bg-amber-50/70 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <History className="h-4 w-4" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-900">
              {isZh
                ? '欢迎回来 — 你之前正在进行一段对话'
                : 'Welcome back — you were exploring something earlier'}
            </p>
            <p className="text-xs text-amber-800/80">
              {isZh
                ? `${userTurns} 轮对话 · ${formatRelative(lastUpdatedAt, true)}${reachedBranch ? ' · 已开始分析' : ''}`
                : `${userTurns} ${userTurns === 1 ? 'turn' : 'turns'} · ${formatRelative(lastUpdatedAt, false)}${reachedBranch ? ' · analysis started' : ''}`}
            </p>
            <p className="text-xs text-amber-800/70">
              {isZh
                ? '你想从上次的地方继续，还是重新开始？'
                : 'Would you like to continue where you left off?'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onStartFresh}
          aria-label={isZh ? '关闭' : 'Dismiss'}
          className="text-amber-700/60 hover:text-amber-900 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 pl-11">
        <Button
          size="sm"
          onClick={onContinue}
          className={
            reachedBranch
              ? 'bg-amber-600 hover:bg-amber-700 text-white'
              : 'bg-amber-500 hover:bg-amber-600 text-white'
          }
        >
          <History className="h-3.5 w-3.5 mr-1.5" />
          {isZh ? '继续上次对话' : 'Continue conversation'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onStartFresh}
          className="border-amber-300 text-amber-800 hover:bg-amber-100"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          {isZh ? '重新开始' : 'Start fresh'}
        </Button>
      </div>
    </div>
  );
};

export default SessionResumePrompt;
